import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail } from "lucide-react";
import { toast } from "sonner";
import { emailResumeLink } from "@/lib/api";

/**
 * Lean modal: one email field → POST /api/drafts/email-link → toast result.
 * Falls back gracefully when the email provider is not yet configured.
 */
export default function EmailResumeLinkDialog({ draftId, resumeUrl, triggerClassName }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!draftId) {
      toast.error("Draft is still being saved — please try again in a moment.");
      return;
    }
    setLoading(true);
    try {
      const res = await emailResumeLink(draftId, email.trim(), resumeUrl);
      toast.success(res?.message || "Request received.");
      setOpen(false);
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not send right now.";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className={triggerClassName || "text-navy hover:bg-gold/10"}
          data-testid="resume-email-link-btn"
        >
          <Mail size={14} className="mr-1.5" /> Email me my resume link
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="resume-email-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-navy">
            Email me my resume link
          </DialogTitle>
          <DialogDescription>
            We'll send the secure resume link only. No will content is shared by email.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="resume-email-input">Your email</Label>
            <Input
              id="resume-email-input"
              type="email"
              autoFocus
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="mt-2"
              data-testid="resume-email-input"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-navy text-white hover:bg-[#1A2642]"
            data-testid="resume-email-submit-btn"
          >
            {loading ? "Sending…" : "Send Resume Link"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
